import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const RES_LABEL = { keep: 'Keep source', '720p': '720p HD', '1080p': '1080p Full HD', '1440p': '1440p QHD', '4k': '4K UHD' };
const STATUS = {
  queued: { label: 'Queued', tone: 'bg-slate-100 text-slate-700 border-slate-200', icon: 'fa-hourglass-start' },
  processing: { label: 'Enhancing', tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'fa-gears' },
  done: { label: 'Enhanced', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'fa-circle-check' },
  error: { label: 'Failed', tone: 'bg-rose-50 text-rose-700 border-rose-200', icon: 'fa-triangle-exclamation' },
};

const mb = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : '—');
const dur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '—');
const dim = (m) => (m?.width ? `${m.width}×${m.height}` : '—');

function Toggle({ on, onChange, label, hint, testid }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testid}
      className={`w-full text-left p-3 rounded-xl border transition-colors ${on ? 'border-emerald-400 bg-emerald-50/60' : 'border-border bg-surface hover:border-primary/40'}`}>
      <div className="flex items-center gap-2">
        <span className={`w-9 h-5 rounded-full relative transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-4.5 ml-0.5' : 'left-0.5'}`} />
        </span>
        <b className="text-sm text-text-primary">{label}</b>
      </div>
      <p className="text-[11px] text-text-muted mt-1 leading-snug">{hint}</p>
    </button>
  );
}

function Compare({ job, k }) {
  const [x, setX] = useState(50);
  const before = `${API_URL}/api/video/jobs/${job.id}/thumb?which=before&k=${k}`;
  const after = `${API_URL}/api/video/jobs/${job.id}/thumb?which=after&k=${k}`;
  return (
    <div className="space-y-2" data-testid={`compare-${job.id}`}>
      <div className="relative w-full overflow-hidden rounded-xl border border-border bg-black select-none" style={{ aspectRatio: '16/9' }}>
        <img src={after} alt="Enhanced still" className="absolute inset-0 w-full h-full object-contain" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${x}%` }}>
          <img src={before} alt="Original still" className="absolute inset-0 w-full h-full object-contain"
            style={{ width: `${10000 / x}%`, maxWidth: 'none' }} />
        </div>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white/90 pointer-events-none" style={{ left: `${x}%` }} />
        <span className="absolute bottom-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/70 text-white">BEFORE</span>
        <span className="absolute bottom-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">AFTER</span>
      </div>
      <input type="range" min="0" max="100" value={x} onChange={(e) => setX(Number(e.target.value))}
        className="w-full accent-emerald-500" data-testid={`compare-slider-${job.id}`} />
    </div>
  );
}

function JobCard({ job, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const s = STATUS[job.status] || STATUS.queued;
  const k = job.share_key;
  const p = job.params || {};
  return (
    <div className="card-interactive p-4 space-y-3" data-testid={`video-job-${job.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <b className="text-sm text-text-primary block truncate" data-testid={`job-name-${job.id}`}>{job.label || job.filename}</b>
          <p className="text-[11px] text-text-muted mt-0.5">
            {dim(job.meta)} · {dur(job.meta?.duration)} · {mb(job.source_size)} → {RES_LABEL[p.resolution] || p.resolution}
            {job.output_meta ? ` · ${dim(job.output_meta)} · ${mb(job.output_size)}` : ''}
          </p>
        </div>
        <span className={`badge ml-auto shrink-0 ${s.tone}`} data-testid={`job-status-${job.id}`}><i className={`fa-solid ${s.icon} mr-1`} />{s.label}</span>
      </div>

      <div className="flex flex-wrap gap-1.5 text-[10px]">
        <span className="badge bg-slate-50 text-slate-600 border-slate-200">{p.preset}</span>
        {p.denoise && <span className="badge bg-slate-50 text-slate-600 border-slate-200">denoise</span>}
        {p.stabilize && <span className="badge bg-slate-50 text-slate-600 border-slate-200">stabilise</span>}
        {p.fps !== 'keep' && <span className="badge bg-slate-50 text-slate-600 border-slate-200">{p.fps} fps</span>}
        {p.audio !== 'keep' && <span className="badge bg-slate-50 text-slate-600 border-slate-200">audio {p.audio}</span>}
      </div>

      {(job.status === 'queued' || job.status === 'processing') && (
        <div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${job.progress || 2}%` }} data-testid={`job-progress-${job.id}`} />
          </div>
          <p className="text-[11px] text-text-muted mt-1">{job.progress || 0}% — ffmpeg is re-encoding frame by frame, longer clips take a few minutes.</p>
        </div>
      )}

      {job.status === 'error' && <p className="text-xs text-rose-600 break-words" data-testid={`job-error-${job.id}`}>{job.error}</p>}

      {job.status === 'done' && (
        <>
          {playing
            ? <video src={`${API_URL}/api/video/jobs/${job.id}/file?k=${k}`} controls autoPlay className="w-full rounded-xl border border-border bg-black" data-testid={`job-player-${job.id}`} />
            : (job.after_path && job.before_path ? <Compare job={job} k={k} /> : null)}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setPlaying(!playing)} className="btn-secondary text-xs" data-testid={`job-play-${job.id}`}>
              <i className={`fa-solid ${playing ? 'fa-images' : 'fa-play'} mr-1`} />{playing ? 'Back to compare' : 'Play enhanced'}
            </button>
            <a href={`${API_URL}/api/video/jobs/${job.id}/download?k=${k}`} className="btn-primary text-xs" data-testid={`job-download-${job.id}`}>
              <i className="fa-solid fa-download mr-1" />Download MP4
            </a>
          </div>
        </>
      )}

      <button onClick={() => onDelete(job)} className="text-[11px] text-text-muted hover:text-rose-600 font-semibold" data-testid={`job-delete-${job.id}`}>
        <i className="fa-solid fa-trash-can mr-1" />Remove
      </button>
    </div>
  );
}

export default function VideoEnhance() {
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState('balanced');
  const [resolution, setResolution] = useState('1080p');
  const [denoise, setDenoise] = useState(false);
  const [stabilize, setStabilize] = useState(false);
  const [fps, setFps] = useState('keep');
  const [audio, setAudio] = useState('keep');
  const [uploading, setUploading] = useState(0);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/video/jobs');
      setJobs(res.data.jobs || []);
    } catch (e) { /* polling */ }
  }, []);

  useEffect(() => {
    api.get('/api/video/presets').then(r => setCfg(r.data)).catch(() => setCfg({ available: false, presets: [] }));
    load();
  }, [load]);

  useEffect(() => {
    if (!jobs.some(j => j.status === 'queued' || j.status === 'processing')) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const pick = (f) => {
    if (!f) return;
    const maxMb = cfg?.max_mb || 150;
    if (f.size > maxMb * 1048576) { toast.error(`That clip is ${(f.size / 1048576).toFixed(0)} MB — the limit is ${maxMb} MB`); return; }
    setFile(f);
    if (!label) setLabel(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    if (!file) { toast.error('Choose a video file first'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preset', preset);
    fd.append('resolution', resolution);
    fd.append('denoise', String(denoise));
    fd.append('stabilize', String(stabilize));
    fd.append('fps', fps);
    fd.append('audio', audio);
    fd.append('label', label);
    setUploading(1);
    try {
      await api.post('/api/video/enhance', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploading(Math.max(1, Math.round((e.loaded / (e.total || 1)) * 100))),
      });
      toast.success('Upload done — enhancing now');
      setFile(null); setLabel('');
      if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(0);
    }
  };

  const remove = async (job) => {
    try {
      await api.delete(`/api/video/jobs/${job.id}`);
      setJobs(j => j.filter(x => x.id !== job.id));
      toast.success('Removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };

  return (
    <div className="space-y-6" data-testid="video-enhance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-text-primary"><i className="fa-solid fa-clapperboard mr-2 text-emerald-500" />Video Enhance</h1>
          <p className="text-sm text-text-muted mt-1">Upscale, sharpen and grade roof walkthroughs, drone flyovers and install footage. Runs locally with ffmpeg — no API keys, no per-minute fees.</p>
        </div>
        {cfg && <span className={`badge ${cfg.available ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`} data-testid="engine-status">
          <i className={`fa-solid ${cfg.available ? 'fa-circle-check' : 'fa-circle-xmark'} mr-1`} />{cfg.available ? 'ffmpeg engine ready' : 'ffmpeg unavailable'}
        </span>}
      </div>

      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-6 items-start">
        <div className="card-interactive p-5 space-y-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${drag ? 'border-emerald-500 bg-emerald-50/60' : 'border-border hover:border-emerald-400'}`}
            data-testid="video-dropzone">
            <i className={`fa-solid ${file ? 'fa-file-video text-emerald-500' : 'fa-cloud-arrow-up text-text-muted'} text-3xl`} />
            <p className="text-sm font-semibold text-text-primary mt-3" data-testid="dropzone-filename">{file ? file.name : 'Drop a clip here or click to browse'}</p>
            <p className="text-[11px] text-text-muted mt-1">{file ? mb(file.size) : `MP4, MOV, WEBM, AVI, MKV · up to ${cfg?.max_mb || 150} MB`}</p>
            <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="video-file-input" />
          </div>

          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. 5 Saddle Ct — roof flyover)"
            className="w-full px-3 py-2.5 rounded-xl border border-border bg-surface text-sm text-text-primary" data-testid="video-label-input" />

          <div className="grid sm:grid-cols-2 gap-2">
            {(cfg?.presets || []).map(p => (
              <button key={p.id} type="button" onClick={() => setPreset(p.id)} data-testid={`preset-${p.id}`}
                className={`text-left p-3 rounded-xl border transition-all ${preset === p.id ? 'border-emerald-500 bg-emerald-50/70 shadow-sm' : 'border-border bg-surface hover:border-emerald-300'}`}>
                <b className="text-sm text-text-primary"><i className={`fa-solid ${p.icon} mr-2 text-emerald-500`} />{p.label}</b>
                <p className="text-[11px] text-text-muted mt-1 leading-snug">{p.desc}</p>
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-[11px] font-bold uppercase tracking-widest text-text-muted">Resolution
              <select value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="resolution-select"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-surface text-sm font-normal normal-case tracking-normal text-text-primary">
                {(cfg?.resolutions || ['keep', '1080p']).map(r => <option key={r} value={r}>{RES_LABEL[r] || r}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-bold uppercase tracking-widest text-text-muted">Frame rate
              <select value={fps} onChange={(e) => setFps(e.target.value)} data-testid="fps-select"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-surface text-sm font-normal normal-case tracking-normal text-text-primary">
                <option value="keep">Keep source</option><option value="30">30 fps</option><option value="60">60 fps</option>
              </select>
            </label>
            <label className="text-[11px] font-bold uppercase tracking-widest text-text-muted">Audio
              <select value={audio} onChange={(e) => setAudio(e.target.value)} data-testid="audio-select"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-surface text-sm font-normal normal-case tracking-normal text-text-primary">
                <option value="keep">Keep</option><option value="boost">Normalise loudness</option><option value="mute">Remove</option>
              </select>
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Toggle on={denoise} onChange={setDenoise} label="Denoise" hint="Cleans phone-camera grain before sharpening. Slower, but worth it on dim footage." testid="denoise-toggle" />
            <Toggle on={stabilize} onChange={setStabilize} label="Stabilise" hint="Smooths handheld shake on walkarounds and ladder shots." testid="stabilize-toggle" />
          </div>

          <button onClick={submit} disabled={!file || uploading > 0 || cfg?.available === false}
            className="btn-primary w-full py-3 disabled:opacity-50" data-testid="enhance-submit">
            {uploading > 0 ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Uploading {uploading}%</> : <><i className="fa-solid fa-wand-magic-sparkles mr-2" />Enhance video</>}
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-text-primary">Your clips</h2>
            <button onClick={load} className="btn-secondary text-xs" data-testid="refresh-jobs"><i className="fa-solid fa-rotate mr-1" />Refresh</button>
          </div>
          {jobs.length === 0
            ? <div className="p-6 rounded-xl border border-dashed border-border text-sm text-text-muted text-center" data-testid="no-video-jobs">
                <i className="fa-solid fa-film text-2xl block mb-2 opacity-50" />No clips yet. Upload a walkthrough and it will show up here with a before / after view.
              </div>
            : jobs.map(j => <JobCard key={j.id} job={j} onDelete={remove} />)}
        </div>
      </div>
    </div>
  );
}
